/**
 * Permission Checking Utilities
 *
 * Functions for checking user permissions in the unified access control system.
 * Supports hierarchical org units (platform -> agency -> department) with
 * permission inheritance.
 */

import logger from '@/lib/logger';
import type {
  UserContext,
  OrgUnitType,
  Role,
  PermissionCheckResult,
  RoleAssignmentCheckResult,
  EffectivePermissions,
} from './types';

// ============================================================================
// Permission Check Functions
// ============================================================================

/**
 * Check if user has a specific permission
 *
 * @param userContext - The user's full context
 * @param permissionCode - The permission code to check (e.g., 'calls.view', 'users.manage')
 * @param orgUnitId - Optional org unit ID to check permission at specific level
 * @returns true if user has the permission
 *
 * @example
 * ```ts
 * // Check platform-level permission
 * hasPermission(userContext, 'settings.admin');
 *
 * // Check permission at specific agency
 * hasPermission(userContext, 'users.create', 'agency-123');
 *
 * // Check permission at specific department
 * hasPermission(userContext, 'reports.view', 'dept-456');
 * ```
 */
export function hasPermission(
  userContext: UserContext | null,
  permissionCode: string,
  orgUnitId?: string
): boolean {
  if (!userContext) {
    return false;
  }

  // Platform admins have all permissions
  if (userContext.is_platform_admin) {
    return true;
  }

  // Check platform-level permissions first
  if (userContext.platform_permissions.includes(permissionCode)) {
    return true;
  }

  // If no specific org unit, check if user has permission anywhere
  if (!orgUnitId) {
    // Check all agencies
    for (const agency of userContext.agencies) {
      if (agency.permissions.includes(permissionCode)) {
        return true;
      }
    }

    // Check all departments
    for (const dept of userContext.departments) {
      if (dept.permissions.includes(permissionCode)) {
        return true;
      }
    }

    return false;
  }

  // Check specific org unit
  const agency = userContext.agencies.find((a) => a.agency_id === orgUnitId);
  if (agency) {
    return agency.permissions.includes(permissionCode);
  }

  const dept = userContext.departments.find((d) => d.department_id === orgUnitId);
  if (dept) {
    // Check department permissions
    if (dept.permissions.includes(permissionCode)) {
      return true;
    }

    // Check parent agency permissions (inheritance)
    const parentAgency = userContext.agencies.find((a) => a.agency_id === dept.agency_id);
    if (parentAgency?.permissions.includes(permissionCode)) {
      return true;
    }
  }

  return false;
}

/**
 * Check if user has ANY of the specified permissions
 *
 * @param userContext - The user's full context
 * @param permissionCodes - Array of permission codes to check
 * @param orgUnitId - Optional org unit ID to check permissions at specific level
 * @returns true if user has at least one of the permissions
 *
 * @example
 * ```ts
 * // User needs either view or edit permission
 * hasAnyPermission(userContext, ['calls.view', 'calls.edit']);
 * ```
 */
export function hasAnyPermission(
  userContext: UserContext | null,
  permissionCodes: string[],
  orgUnitId?: string
): boolean {
  if (!userContext || permissionCodes.length === 0) {
    return false;
  }

  return permissionCodes.some((code) => hasPermission(userContext, code, orgUnitId));
}

/**
 * Check if user has ALL of the specified permissions
 *
 * @param userContext - The user's full context
 * @param permissionCodes - Array of permission codes to check
 * @param orgUnitId - Optional org unit ID to check permissions at specific level
 * @returns true if user has all of the permissions
 *
 * @example
 * ```ts
 * // User needs both permissions to perform action
 * hasAllPermissions(userContext, ['users.view', 'users.edit']);
 * ```
 */
export function hasAllPermissions(
  userContext: UserContext | null,
  permissionCodes: string[],
  orgUnitId?: string
): boolean {
  if (!userContext || permissionCodes.length === 0) {
    return false;
  }

  return permissionCodes.every((code) => hasPermission(userContext, code, orgUnitId));
}

/**
 * Get all effective permissions for a user at an org unit
 *
 * Resolves permission inheritance from parent org units.
 *
 * @param userContext - The user's full context
 * @param orgUnitId - The org unit ID to get permissions for (null for platform)
 * @returns Effective permissions with inheritance info
 *
 * @example
 * ```ts
 * // Get permissions at agency level
 * const perms = getEffectivePermissions(userContext, 'agency-123');
 * logger.debug('Permissions', 'Log', { data: perms.permissions }); // ['calls.view', 'calls.create', ...]
 * ```
 */
export function getEffectivePermissions(
  userContext: UserContext | null,
  orgUnitId: string | null
): EffectivePermissions {
  const emptyResult: EffectivePermissions = {
    org_unit_type: 'platform',
    org_unit_id: null,
    permissions: [],
    roles: [],
    inherited_from: null,
  };

  if (!userContext) {
    return emptyResult;
  }

  // Platform level (no org unit specified)
  if (!orgUnitId) {
    return {
      org_unit_type: 'platform',
      org_unit_id: null,
      permissions: [...userContext.platform_permissions],
      roles: [...userContext.platform_roles],
      inherited_from: null,
    };
  }

  // Check if it's an agency
  const agency = userContext.agencies.find((a) => a.agency_id === orgUnitId);
  if (agency) {
    // Combine agency permissions with inherited platform permissions
    const combinedPermissions = new Set<string>([
      ...userContext.platform_permissions,
      ...agency.permissions,
    ]);

    const combinedRoles = [...userContext.platform_roles, ...agency.roles];

    return {
      org_unit_type: 'agency',
      org_unit_id: agency.agency_id,
      permissions: Array.from(combinedPermissions),
      roles: combinedRoles,
      inherited_from: userContext.platform_permissions.length > 0 ? 'platform' : null,
    };
  }

  // Check if it's a department
  const dept = userContext.departments.find((d) => d.department_id === orgUnitId);
  if (dept) {
    const parentAgency = userContext.agencies.find((a) => a.agency_id === dept.agency_id);

    // Combine department + agency + platform permissions
    const combinedPermissions = new Set<string>([
      ...userContext.platform_permissions,
      ...(parentAgency?.permissions || []),
      ...dept.permissions,
    ]);

    const combinedRoles = [
      ...userContext.platform_roles,
      ...(parentAgency?.roles || []),
      ...dept.roles,
    ];

    let inheritedFrom: OrgUnitType | null = null;
    if (parentAgency?.permissions.length) {
      inheritedFrom = 'agency';
    } else if (userContext.platform_permissions.length) {
      inheritedFrom = 'platform';
    }

    return {
      org_unit_type: 'department',
      org_unit_id: dept.department_id,
      permissions: Array.from(combinedPermissions),
      roles: combinedRoles,
      inherited_from: inheritedFrom,
    };
  }

  return emptyResult;
}

/**
 * Check if user can assign a specific role to another user
 *
 * Implements privilege escalation prevention:
 * - Users cannot assign roles with higher hierarchy level than their own
 * - Users can only assign roles they themselves have or lower
 * - Platform admins can assign any role
 *
 * @param userContext - The assigning user's context
 * @param roleId - The role ID to be assigned
 * @param orgUnitId - The org unit where role would be assigned
 * @returns Check result with details
 *
 * @example
 * ```ts
 * const result = canAssignRole(userContext, 'role-supervisor', 'dept-123');
 * if (!result.allowed) {
 *   logger.error('Permissions', 'Error', { data: result.reason }); // "Cannot assign role with higher hierarchy level"
 * }
 * ```
 */
export function canAssignRole(
  userContext: UserContext | null,
  roleId: string,
  orgUnitId?: string
): RoleAssignmentCheckResult {
  if (!userContext) {
    return {
      allowed: false,
      reason: 'User context not available',
    };
  }

  // Platform admins can assign any role
  if (userContext.is_platform_admin) {
    return { allowed: true };
  }

  // Get effective permissions at the org unit
  const effectivePerms = getEffectivePermissions(userContext, orgUnitId || null);

  // User needs 'roles.assign' permission
  if (!effectivePerms.permissions.includes('roles.assign')) {
    return {
      allowed: false,
      reason: 'Missing roles.assign permission',
      missing_permission: 'roles.assign',
    };
  }

  // Find the target role in user's available roles
  const targetRole = findRoleById(userContext, roleId);
  if (!targetRole) {
    return {
      allowed: false,
      reason: 'Target role not found or not accessible',
    };
  }

  // Get user's highest hierarchy level at this org unit
  const userMaxLevel = getMaxHierarchyLevel(effectivePerms.roles);

  // Check for privilege escalation
  if (targetRole.privilege_level > userMaxLevel) {
    return {
      allowed: false,
      reason: 'Cannot assign role with higher privilege level',
      would_escalate: true,
      assigner_level: userMaxLevel,
      target_level: targetRole.privilege_level,
    };
  }

  return { allowed: true };
}

// ============================================================================
// Extended Permission Check Functions
// ============================================================================

/**
 * Check permission with detailed result
 *
 * @param userContext - The user's full context
 * @param permissionCode - The permission code to check
 * @param orgUnitId - Optional org unit ID
 * @returns Detailed check result
 */
export function checkPermission(
  userContext: UserContext | null,
  permissionCode: string,
  orgUnitId?: string
): PermissionCheckResult {
  if (!userContext) {
    return {
      allowed: false,
      reason: 'User context not available',
    };
  }

  const allowed = hasPermission(userContext, permissionCode, orgUnitId);

  if (!allowed) {
    return {
      allowed: false,
      reason: `Missing required permission: ${permissionCode}`,
      missing_permission: permissionCode,
    };
  }

  return { allowed: true };
}

/**
 * Check if user has a specific role
 *
 * @param userContext - The user's full context
 * @param roleName - The role name to check
 * @param orgUnitId - Optional org unit ID
 * @returns true if user has the role
 */
export function hasRole(
  userContext: UserContext | null,
  roleName: string,
  orgUnitId?: string
): boolean {
  if (!userContext) {
    return false;
  }

  // Platform admins effectively have all roles
  if (userContext.is_platform_admin) {
    return true;
  }

  // Get effective permissions at the org unit
  const effectivePerms = getEffectivePermissions(userContext, orgUnitId || null);

  return effectivePerms.roles.some(
    (r) => r.name === roleName || r.slug === roleName
  );
}

/**
 * Get all roles for a user at an org unit
 *
 * @param userContext - The user's full context
 * @param orgUnitId - Optional org unit ID
 * @returns Array of roles
 */
export function getRoles(
  userContext: UserContext | null,
  orgUnitId?: string
): Role[] {
  if (!userContext) {
    return [];
  }

  const effectivePerms = getEffectivePermissions(userContext, orgUnitId || null);
  return effectivePerms.roles;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Find a role by ID in the user's context
 */
function findRoleById(userContext: UserContext, roleId: string): Role | undefined {
  // Check platform roles
  const platformRole = userContext.platform_roles.find((r) => r.id === roleId);
  if (platformRole) {
    return platformRole;
  }

  // Check agency roles
  for (const agency of userContext.agencies) {
    const agencyRole = agency.roles.find((r) => r.id === roleId);
    if (agencyRole) {
      return agencyRole;
    }
  }

  // Check department roles
  for (const dept of userContext.departments) {
    const deptRole = dept.roles.find((r) => r.id === roleId);
    if (deptRole) {
      return deptRole;
    }
  }

  return undefined;
}

/**
 * Get the maximum privilege level from an array of roles
 */
function getMaxHierarchyLevel(roles: Role[]): number {
  if (roles.length === 0) {
    return 0;
  }

  return Math.max(...roles.map((r) => r.privilege_level));
}

/**
 * Check if user is a platform admin
 */
export function isPlatformAdmin(userContext: UserContext | null): boolean {
  return userContext?.is_platform_admin ?? false;
}

/**
 * Check if user is a tenant admin (agency-level admin)
 */
export function isTenantAdmin(userContext: UserContext | null): boolean {
  return userContext?.is_tenant_admin ?? false;
}

/**
 * Check if user has admin access at any level
 */
export function isAnyAdmin(userContext: UserContext | null): boolean {
  if (!userContext) {
    return false;
  }

  return userContext.is_platform_admin || userContext.is_tenant_admin;
}
